import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  OrderUnitOfMeasure,
  postV1CompaniesCompanyIdTickets,
  postV1CompaniesCompanyIdTicketsTicketIdImage,
  TicketSource,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useMemo, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';

import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import {
  LoadingSpinner,
  SmallButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { useDirectUploads } from '~hooks/useDirectUploads/useDirectUploads';
import useEffectiveCompanyId from '~hooks/useEffectiveCompanyId/useEffectiveCompanyId';
import { Load } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

import { ImageUploaderDashedBox } from './styled';

const DEFAULT_MAX_FILE_SIZE_IN_MB = 10;
const allowedImageExtensions = ['png', 'jpeg', 'jpg', 'pdf'];

enum ProcessStep {
  CREATING_TICKET = 'creating-image',
  UPLOADING_IMAGE = 'uploading-image',
}

enum InvalidImageError {
  FILE_SIZE_TOO_LARGE = 'file-size-too-large',
  FILE_TYPE_NOT_ALLOWED = 'file-type-not-allowed',
}

interface ImageUploaderProps {
  isDisabled?: boolean;
  load: Load;
  onTicketImageStateChange: (isManipulatinImage: boolean) => void;
  refreshTicket: (ticket: Ticket, isNewTicket?: boolean) => void;
  sx?: SxProps<Theme>;
  ticket?: Ticket;
}

export default function ImageUploader({
  isDisabled,
  load,
  onTicketImageStateChange,
  refreshTicket,
  sx,
  ticket,
}: ImageUploaderProps) {
  const { toasterStore, ticketsStore } = useStores();
  const companyId = useEffectiveCompanyId();
  const { createDirectUpload } = useDirectUploads();

  const [processStep, setProcessStep] = useState<ProcessStep>();
  const [invalidImageError, setInvalidImageError] = useState<InvalidImageError>();
  const [isDragging, setIsDragging] = useState(false);

  const shouldDisableFileUpload = isDisabled || Boolean(processStep);

  const errorMessage = useMemo(() => {
    if (invalidImageError === InvalidImageError.FILE_SIZE_TOO_LARGE) {
      return t('file_upload.max_size', { maxSize: DEFAULT_MAX_FILE_SIZE_IN_MB + ' MB' });
    }

    if (invalidImageError === InvalidImageError.FILE_TYPE_NOT_ALLOWED) {
      return t('ticket_drawer_v2.image_uploader.invalid_file_type_error', {
        extensions: allowedImageExtensions.join(', '),
      });
    }
  }, [invalidImageError]);

  const showInvalidImageError = useCallback((error: InvalidImageError) => {
    setInvalidImageError(undefined);

    // this is to force a re-render of the error indicator so to reset the animation
    setTimeout(() => setInvalidImageError(error), 0);
  }, []);

  const createTicket = useCallback(async () => {
    try {
      const response = await postV1CompaniesCompanyIdTickets({
        path: { 'company-id': companyId },
        body: {
          job_id: load.jobId,
          load_id: load.id,
          unit_of_measure: OrderUnitOfMeasure.LOAD,
          source: TicketSource.WEB,
        },
      });

      return Ticket.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.tickets.failed_to_create'));
    }
  }, [companyId, load.jobId, load.id]);

  const uploadImage = useCallback(
    async (ticketId: string, file: File): Promise<Ticket> => {
      try {
        // Upload via DirectUpload and send the resulting signed_id — the endpoint's
        // generated client serializes the JSON body, so a raw File would stringify to
        // `{}` and 422 (`File can't be blank`). See TRE-15769 / CUS-393.
        const directUpload = await createDirectUpload(file);
        const response = await postV1CompaniesCompanyIdTicketsTicketIdImage({
          path: {
            'company-id': companyId,
            'ticket-id': ticketId,
          },
          body: { image: directUpload.signed_id },
        });

        return Ticket.parse(response.data.data);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.tickets.failed_to_upload_ticket_image'),
        );
        throw error;
      }
    },
    [companyId, createDirectUpload],
  );

  const handleChange = useCallback(
    async (file: File) => {
      setInvalidImageError(undefined);

      if (shouldDisableFileUpload) {
        return;
      }

      try {
        const ticketId = ticket?.id;

        onTicketImageStateChange(true);

        if (ticketId) {
          setProcessStep(ProcessStep.UPLOADING_IMAGE);
          const updatedTicket = await uploadImage(ticketId, file);
          ticketsStore.ticketAnalysisStart(ticketId);
          refreshTicket(updatedTicket);
          toasterStore.push(alert(t('approvals.ticket_updated'), AlertTypes.success));
        } else {
          setProcessStep(ProcessStep.CREATING_TICKET);
          let newTicket = await createTicket();

          if (newTicket) {
            setProcessStep(ProcessStep.UPLOADING_IMAGE);
            newTicket = await uploadImage(newTicket.id, file);
            ticketsStore.ticketAnalysisStart(newTicket.id);
            refreshTicket(newTicket, true);
            toasterStore.push(alert(t('approvals.ticket_created'), AlertTypes.success));
          }
        }
      } finally {
        setProcessStep(undefined);
        onTicketImageStateChange(false);
      }
    },
    [shouldDisableFileUpload, ticket?.id, onTicketImageStateChange, refreshTicket],
  );

  return (
    <Box>
      <FileUploader
        disabled={shouldDisableFileUpload}
        dropMessageStyle={{ opacity: 0 }}
        handleChange={handleChange}
        maxSize={DEFAULT_MAX_FILE_SIZE_IN_MB}
        name="ticket-image-uploader"
        onDraggingStateChange={(dragging: boolean) => setIsDragging(dragging)}
        onSizeError={() => showInvalidImageError(InvalidImageError.FILE_SIZE_TOO_LARGE)}
        onTypeError={() => showInvalidImageError(InvalidImageError.FILE_TYPE_NOT_ALLOWED)}
        types={allowedImageExtensions}
      >
        {processStep ? (
          <LoadingIndicator sx={sx} step={processStep} />
        ) : (
          <NoImageIndicator
            isDisabled={shouldDisableFileUpload}
            isDragging={isDragging}
            sx={sx}
          />
        )}
      </FileUploader>

      {errorMessage && <ShakyErrorIndicator message={errorMessage} sx={{ mt: 1 }} />}
    </Box>
  );
}

interface NoImageIndicatorProps {
  isDisabled?: boolean;
  isDragging?: boolean;
  sx?: SxProps<Theme>;
}

function NoImageIndicator({ isDisabled, isDragging, sx }: NoImageIndicatorProps) {
  const isReallyDragging = isDisabled ? false : isDragging;

  return (
    <ImageUploaderDashedBox
      isDisabled={isDisabled}
      sx={[
        (theme) => ({
          height: '300px',
          '&:hover': isDisabled
            ? {}
            : {
                borderColor: theme.brandV2.colors.treadYellowDark,
                '.no-image-indicator-content': { color: theme.brandV2.colors.treadBlack },
              },
          ...(isReallyDragging && { borderColor: theme.brandV2.colors.treadOrangeDark }),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {isReallyDragging ? (
        <CloudUploadOutlined
          sx={(theme) => ({ fontSize: '40px', color: theme.brandV2.colors.treadBlack })}
        />
      ) : (
        <FileUploadOutlined
          className="no-image-indicator-content"
          sx={(theme) => ({ fontSize: '40px', color: theme.brandV2.colors.treadGray6 })}
        />
      )}

      <Typography
        sx={(theme) => ({
          color: theme.brandV2.colors.treadBlack,
          fontSize: '14px',
          fontWeight: 600,
          textAlign: 'center',
        })}
      >
        {isReallyDragging
          ? t('ticket_drawer_v2.image_uploader.drop_to_upload')
          : t('file_upload.click_add_or_drag')}
      </Typography>

      <>
        <Typography
          sx={(theme) => ({
            color: theme.brandV2.colors.treadGray3,
            fontSize: '12px',
            fontWeight: 600,
            textAlign: 'center',
          })}
        >
          {`${allowedImageExtensions.join(', ').toUpperCase()}. ${t('file_upload.max_size', { maxSize: DEFAULT_MAX_FILE_SIZE_IN_MB })} MB.`}
        </Typography>

        <SmallButton
          color="brandV2Yellow"
          component="span"
          disabled={isDisabled}
          sx={{ mt: 1 }}
        >
          {t('file_upload.upload')}
        </SmallButton>
      </>
    </ImageUploaderDashedBox>
  );
}

interface LoadingIndicatorProps {
  step: ProcessStep;
  sx?: SxProps<Theme>;
}

function LoadingIndicator({ step, sx }: LoadingIndicatorProps) {
  const message =
    step === ProcessStep.CREATING_TICKET
      ? t('ticket_drawer_v2.image_uploader.creating_ticket')
      : t('ticket_drawer_v2.image_uploader.uploading_image');

  return (
    <ImageUploaderDashedBox isDisabled sx={{ ...sx, height: '300px' }}>
      <LoadingSpinner isVisible />

      <Typography
        sx={(theme) => ({
          color: theme.brandV2.colors.treadGray2,
          fontSize: '12px',
          fontWeight: 600,
          textAlign: 'center',
        })}
      >
        {message}
      </Typography>
    </ImageUploaderDashedBox>
  );
}
